import {
  useCallback,
  useContext,
  createContext,
  ReactNode,
  useState,
} from "react";

import invariant from "tiny-invariant";

import { CfSnackbar } from "../../components/common/CfSnackbar/CfSnackbar";

interface SnackbarProps {
  autoHideDuration?: number;
  isError?: boolean;
  isSuccess?: boolean;
  message: ReactNode;
}

const SnackbarContext = createContext<
  ((snackbarProps: SnackbarProps) => void) | undefined
>(undefined);

interface SnackbarProviderProps {
  children: ReactNode;
}

const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [snackbarData, setSnackbarData] = useState<SnackbarProps | null>(null);

  const showSnackbar = useCallback((snackbarProps: SnackbarProps) => {
    setSnackbarData(snackbarProps);
  }, []);

  const handleClose = () => {
    setSnackbarData(null);
  };

  return (
    <>
      {snackbarData && (
        <CfSnackbar
          autoHideDuration={snackbarData.autoHideDuration}
          isError={snackbarData.isError}
          isSuccess={snackbarData.isSuccess}
          message={snackbarData.message}
          onClose={handleClose}
          open={true}
        />
      )}
      <SnackbarContext.Provider value={showSnackbar}>
        {children}
      </SnackbarContext.Provider>
    </>
  );
};

const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  invariant(context, "useSnackbar must be used within a SnackbarProvider");

  return context;
};

export { SnackbarProvider, useSnackbar };
